/**
 * Assistant Overlay Store
 *
 * UI state for the system-wide contextual assistant popout: open/closed,
 * panel width, the "include screen context" preference, and conversation
 * scope (global vs. reset-per-page). Persisted to localStorage so the
 * panel remembers its size and preferences (but not open/closed) across
 * reloads.
 *
 * No domain logic — pure infrastructure.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;

/**
 * Conversation scope:
 *   - 'global'   — one conversation follows the user across every page.
 *   - 'per-page' — the conversation resets (via the adapter's own
 *     resetConversation(), a fresh session, not a persisted per-page
 *     history) whenever the active page changes. There's no adapter-level
 *     support for storing/restoring separate histories per page, so this
 *     is "start fresh on navigate", not resumable per-page threads.
 */
export type ConversationScope = 'global' | 'per-page';

interface AssistantOverlayState {
  isOpen: boolean;
  width: number;
  /** Whether to attach the current screen's context to each chat turn. */
  includeScreenContext: boolean;
  conversationScope: ConversationScope;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  setIncludeScreenContext: (value: boolean) => void;
  setConversationScope: (value: ConversationScope) => void;
}

export const useAssistantOverlayStore = create<AssistantOverlayState>()(
  persist(
    (set) => ({
      isOpen: false,
      width: DEFAULT_WIDTH,
      includeScreenContext: true,
      conversationScope: 'global',

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      setWidth: (width) =>
        set({ width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width)) }),
      setIncludeScreenContext: (value) => set({ includeScreenContext: value }),
      setConversationScope: (value) => set({ conversationScope: value }),
    }),
    {
      name: 'neuro-assistant-overlay',
      partialize: (state) => ({
        width: state.width,
        includeScreenContext: state.includeScreenContext,
        conversationScope: state.conversationScope,
      }),
    },
  ),
);
